var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { ErrorMessage, Footnote, Label as LabelTop, LabelSide, LabelContentSpanLeft, LabelContentSpanRight, LabelContentSpanTop, LabelWrapperChildren, LabelWrapChildrenSide, LabelWrapChildren as LabelWrapChildrenTop, InfoIcon, ValidationLabel, } from './Input.styled';
import React from 'react';
import { cx, css as emotionCss } from 'emotion';
import { getAutomation, getDebug, getStyleNames, useReadSessionStorage } from '@citrus/runtime';
import { useLocalization, useTheme } from '@citrus/user';
import { iif, isNotNullOrEmpty, isNullOrEmpty } from '@pcty/utils';
import { Tooltip } from '@citrus/tooltip';
import { CharacterCountContext } from './private/context';
import { disabledLabel, disabledFootnote as disabledFootnoteCitron } from '../styles/citron';
import localizationMap from '../localization';
import { disabledFootnote as disabledFootnoteLegacy } from '../styles/legacy';
var VERSION = '3.7.2';
var Input = React.forwardRef(function (_a, ref) {
    var _b, _c;
    var _d;
    var automationId = _a.automationId, _e = _a.className, className = _e === void 0 ? '' : _e, css = _a.css, children = _a.children, _f = _a.disabled, disabled = _f === void 0 ? false : _f, _g = _a.emptyLabel, emptyLabel = _g === void 0 ? false : _g, errorMessage = _a.errorMessage, footnote = _a.footnote, _h = _a.hideOptionalLabel, hideOptionalLabel = _h === void 0 ? false : _h, htmlFor = _a.htmlFor, id = _a.id, _j = _a.ignoreWrappedClick, ignoreWrappedClick = _j === void 0 ? false : _j, label = _a.label, _k = _a.labelAddOn, labelAddOn = _k === void 0 ? function () { return null; } : _k, _l = _a.labelPosition, labelPosition = _l === void 0 ? 'top' : _l, _m = _a.readOnly, readOnly = _m === void 0 ? false : _m, _o = _a.renderCharacterCount, renderCharacterCount = _o === void 0 ? function () { return null; } : _o, _p = _a.required, required = _p === void 0 ? false : _p, _q = _a.showRequiredLabel, showRequiredLabel = _q === void 0 ? false : _q, _r = _a.showValidationState, showValidationState = _r === void 0 ? false : _r, tooltip = _a.tooltip, _s = _a.valid, valid = _s === void 0 ? false : _s, _t = _a.wrapChildren, wrapChildren = _t === void 0 ? true : _t, styleNames = _a.styleNames, _u = _a.__TYPE, __TYPE = _u === void 0 ? 'Input' : _u, other = __rest(_a, ["automationId", "className", "css", "children", "disabled", "emptyLabel", "errorMessage", "footnote", "hideOptionalLabel", "htmlFor", "id", "ignoreWrappedClick", "label", "labelAddOn", "labelPosition", "readOnly", "renderCharacterCount", "required", "showRequiredLabel", "showValidationState", "tooltip", "valid", "wrapChildren", "styleNames", "__TYPE"]);
    var lang = useLocalization(localizationMap);
    var debug = useReadSessionStorage('citrus-debug', 'false');
    var isCitron = useTheme().citrus2022;
    var classesObjLabel = (_b = {},
        _b[emotionCss(disabledLabel)] = disabled && isCitron,
        _b);
    var classesLabel = cx(classesObjLabel, className || '');
    var styleNamesLabel = {
        disabledLabel: (_d = classesObjLabel[emotionCss(disabledLabel)]) !== null && _d !== void 0 ? _d : false,
    };
    var processLabelWrapperClick = function (e) {
        if (ignoreWrappedClick) {
            var acceptableTags = ['INPUT', 'TEXTAREA', 'DIV'];
            if (!acceptableTags.includes(e.target.tagName)) {
                e.preventDefault();
            }
        }
    };
    var renderFormLabel = function () {
        var _labelAddOn;
        if (typeof labelAddOn === 'string' && labelAddOn === 'about') {
            _labelAddOn = React.createElement(InfoIcon, null);
        }
        else if (typeof labelAddOn === 'function') {
            _labelAddOn = labelAddOn();
        }
        else
            _labelAddOn = undefined;
        var _htmlFor;
        if (htmlFor === '') {
            _htmlFor = undefined;
        }
        else if (isNotNullOrEmpty(_htmlFor)) {
            _htmlFor = _htmlFor;
        }
        else {
            _htmlFor = id;
        }
        if (!label && !_labelAddOn && !emptyLabel) {
            return undefined;
        }
        var Label = labelPosition === 'top' ? LabelTop : LabelSide;
        var LabelContentSpan = labelPosition === 'top'
            ? LabelContentSpanTop
            : iif(labelPosition === 'left', LabelContentSpanLeft, LabelContentSpanRight);
        var LabelWrapChildren = labelPosition === 'top' ? LabelWrapChildrenTop : LabelWrapChildrenSide;
        var tooltipId = "input-tooltip-".concat(id);
        var renderValidationLabel = function () {
            var labelText = '';
            var isRequiredLabel = required && showRequiredLabel && !readOnly;
            var isOptionalLabel = !required && !hideOptionalLabel && !readOnly;
            if (isRequiredLabel)
                labelText = lang.required;
            else if (isOptionalLabel)
                labelText = lang.optional;
            return isNotNullOrEmpty(labelText) ? React.createElement(ValidationLabel, null, labelText) : React.createElement(React.Fragment, null);
        };
        var labelContent = (React.createElement(LabelContentSpan, __assign({ className: classesLabel, styleNames: getStyleNames(styleNamesLabel) }, getAutomation(automationId, 'label-span')),
            label || (emptyLabel && required ? React.createElement(React.Fragment, null, "\u00A0") : ''),
            renderValidationLabel(),
            tooltip && (React.createElement(React.Fragment, null,
                React.createElement("a", { "aria-label": "input more information", "data-citrus-tooltip": tooltipId, title: "Input more Information" },
                    React.createElement(InfoIcon, null)),
                React.createElement(Tooltip, { id: tooltipId, location: "top" }, tooltip))),
            _labelAddOn));
        if (wrapChildren) {
            return (React.createElement(LabelWrapperChildren, null, (label || emptyLabel) && (React.createElement(LabelWrapChildren, __assign({ htmlFor: _htmlFor, onClick: processLabelWrapperClick }, getAutomation(automationId, 'label')),
                labelPosition === 'right' ? children : labelContent,
                labelPosition === 'right' ? labelContent : children))));
        }
        return (React.createElement(Label, __assign({ className: classesLabel }, getDebug(debug === 'true', __TYPE, VERSION), (debug ? { 'data-style': getStyleNames(styleNamesLabel) } : {})),
            (label || emptyLabel) && (React.createElement("label", __assign({ htmlFor: _htmlFor, onClick: processLabelWrapperClick }, getAutomation(automationId, 'label')),
                label || (emptyLabel && required ? React.createElement(React.Fragment, null, "\u00A0") : ''),
                required && (React.createElement("em", null,
                    emptyLabel && isNullOrEmpty(label) ? '' : ' ',
                    showRequiredLabel && !readOnly && lang.required)),
                !required && (React.createElement("em", null,
                    emptyLabel && isNullOrEmpty(label) ? '' : ' ',
                    !hideOptionalLabel && !readOnly && lang.optional)),
                tooltip && (React.createElement(React.Fragment, null,
                    React.createElement("a", { "aria-label": "input more information", "data-citrus-tooltip": tooltipId, title: "Input more Information" },
                        React.createElement(InfoIcon, null)),
                    React.createElement(Tooltip, { id: tooltipId, location: "top" }, tooltip))))),
            _labelAddOn,
            children));
    };
    var renderRemainingCharacters = function () { return (renderCharacterCount ? renderCharacterCount() : undefined); };
    var renderErrorMessage = function () {
        if (!valid && showValidationState && errorMessage && errorMessage.length > 0) {
            return (React.createElement(ErrorMessage, __assign({ style: !footnote ? { marginTop: '8px' } : undefined, id: id + '-error-message' }, getAutomation(automationId, 'error')), errorMessage));
        }
    };
    var renderFootnote = function () {
        var _a;
        var _b;
        if (footnote) {
            var disabledFootnote = (isCitron
                ? disabledFootnoteCitron
                : disabledFootnoteLegacy);
            var classesObjFootnote = (_a = {},
                _a[emotionCss(disabledFootnote)] = disabled,
                _a);
            var classesFootnote = cx(classesObjFootnote);
            var styleNamesFootnote = {
                disabledFootnote: (_b = classesObjFootnote[emotionCss(disabledFootnote)]) !== null && _b !== void 0 ? _b : false,
            };
            return (React.createElement(Footnote, __assign({ className: classesFootnote }, getAutomation(automationId, 'footnote'), getDebug(debug === 'true', __TYPE, VERSION), (debug ? { 'data-style': getStyleNames(styleNamesFootnote) } : {})), footnote));
        }
    };
    var classes = cx((_c = {},
        _c[emotionCss(css)] = isNotNullOrEmpty(css),
        _c[emotionCss(disabledLabel)] = disabled && isCitron,
        _c), className || '');
    var wrapperProps = __assign(__assign(__assign(__assign({}, getAutomation(automationId, 'wrapper')), getDebug(sessionStorage.getItem('citrus-debug') === 'true', __TYPE, VERSION)), (sessionStorage.getItem('citrus-debug') === 'true' ? { 'data-style': styleNames } : {})), other);
    if (labelPosition === 'left') {
        return (React.createElement(CharacterCountContext.Provider, { value: { character: lang.character, characters: lang.characters, remaining: lang.remaining } },
            React.createElement("div", __assign({ className: classes }, wrapperProps, { ref: ref }),
                !wrapChildren && children,
                renderFormLabel(),
                renderRemainingCharacters(),
                renderFootnote(),
                renderErrorMessage())));
    }
    return (React.createElement(CharacterCountContext.Provider, { value: { character: lang.character, characters: lang.characters, remaining: lang.remaining } },
        React.createElement("div", __assign({ className: classes }, wrapperProps, { ref: ref }),
            renderFormLabel(),
            (!wrapChildren || (isNullOrEmpty(label) && !emptyLabel)) && children,
            renderRemainingCharacters(),
            renderFootnote(),
            renderErrorMessage())));
});
export default Input;
